/**
 * Codec negotiation and the constant-size frame layout (docs/protocol/audio-codec-v2.md).
 *
 * Kept as a dependency-free module because the same rules are hand-ported to
 * Swift and Kotlin. If the two sides ever pick different codecs the call
 * connects and then stays silent, with nothing in any log to say why — so the
 * selection is a pure function of the two advertised lists, testable here and
 * mirrored exactly there.
 */

/** The one preference order both peers use. Never either peer's own ordering. */
export const CODEC_PREFERENCE = ['opus', 'mulaw'];

/** What this build can decode, best first. */
export const SUPPORTED_CODECS = ['opus', 'mulaw'];

export const FRAME_AUDIO = 0x01;
export const FRAME_COMFORT = 0x00;

/**
 * Constant plaintext size per codec. Every frame is padded to it, so the wire
 * looks identical whether someone is speaking, silent, or muted.
 *
 * opus: 192 leaves room for 189 bytes of payload (~75 kbit/s), so the bitrate
 * can be raised later without changing anything an observer can see.
 */
export const PLAINTEXT_SIZE = { opus: 192, mulaw: 1024 };

/** Byte offset where the payload starts: opus carries a 2-byte length, mu-law does not. */
export const PAYLOAD_OFFSET = { opus: 3, mulaw: 1 };

export const MULAW_SIZE = 960;
export const MAX_OPUS_PAYLOAD = PLAINTEXT_SIZE.opus - PAYLOAD_OFFSET.opus;

/** 20 ms at 48 kHz. Unchanged by this migration, which keeps the rate at 50 pps. */
export const FRAME_SAMPLES = 960;
export const SAMPLE_RATE = 48000;
export const OPUS_BITRATE = 24000;

/**
 * What a peer can decode. An absent or unusable list means a v1 client or an
 * old guest, which predates the field and only ever spoke mu-law.
 */
export function peerCodecs(advertised) {
  if (!Array.isArray(advertised) || advertised.length === 0) return ['mulaw'];
  const clean = advertised.filter(c => typeof c === 'string' && CODEC_PREFERENCE.includes(c));
  return clean.length > 0 ? clean : ['mulaw'];
}

/**
 * The codec to ENCODE with. Both peers run this with mirrored arguments and
 * must reach the same answer, which is why the result is ordered by
 * CODEC_PREFERENCE rather than by whoever's list came first.
 */
export function selectCodec(mine = SUPPORTED_CODECS, theirs) {
  const ours = peerCodecs(mine);
  const peer = peerCodecs(theirs);
  for (const codec of CODEC_PREFERENCE) {
    if (ours.includes(codec) && peer.includes(codec)) return codec;
  }
  // No overlap should be impossible while both sides list mu-law, but falling
  // back to silence would be worse than falling back to the codec everyone has.
  return 'mulaw';
}

/**
 * The decoder for a received frame, chosen by plaintext length rather than by
 * what we negotiated. Negotiation governs sending only; dispatching on length
 * means a frame that arrives before `tunnel_peer`, or a peer that disagrees
 * with us, is still heard instead of dropped.
 */
export function codecForPlaintext(length) {
  for (const [codec, size] of Object.entries(PLAINTEXT_SIZE)) {
    if (length === size) return codec;
  }
  return null;
}

/**
 * Build a padded plaintext. `payload` may be empty for a comfort frame, though
 * with Opus a comfort frame carries encoded silence so the decoder keeps its
 * continuity — see the ADR.
 *
 * `randomBytes(n)` is injected so this stays testable and so each platform uses
 * its own CSPRNG. The padding is random rather than zero so that a plaintext
 * never has a predictable tail, which matters if the cipher is ever changed.
 */
export function buildPlaintext(codec, payload, flags, randomBytes) {
  const size = PLAINTEXT_SIZE[codec];
  if (!size) throw new Error(`Unknown codec: ${codec}`);
  const offset = PAYLOAD_OFFSET[codec];
  const capacity = size - offset;
  if (payload && payload.length > capacity) {
    throw new Error(`${codec} payload of ${payload.length}B exceeds the ${capacity}B frame`);
  }

  const out = new Uint8Array(size);
  out.set(randomBytes(size), 0);          // pad first, then overwrite the header
  out[0] = flags;
  const len = payload ? payload.length : 0;
  if (codec === 'opus') {
    out[1] = len & 0xff;
    out[2] = (len >> 8) & 0xff;
  }
  if (len > 0) out.set(payload, offset);
  else if (codec === 'mulaw') out.fill(0, 1, 1 + MULAW_SIZE);  // v1 comfort frame is zeroed
  return out;
}

/**
 * Split a received plaintext. Returns null when the frame is too short or
 * claims a length that does not fit — one malformed frame must cost one frame,
 * never the call.
 */
export function parsePlaintext(plaintext) {
  const codec = codecForPlaintext(plaintext.length);
  if (!codec) return null;
  const flags = plaintext[0];
  if (codec === 'mulaw') {
    return { codec, flags, payload: plaintext.subarray(1, 1 + MULAW_SIZE) };
  }
  const len = plaintext[1] | (plaintext[2] << 8);
  if (len > MAX_OPUS_PAYLOAD) return null;
  return { codec, flags, payload: plaintext.subarray(3, 3 + len) };
}
